import React, { useState, useEffect } from "react";
import AuthDebug from "./pages/AuthDebug";
import AuthCallback from "./pages/AuthCallback";
import AdminInvites from "./pages/AdminInvites";
import PublicMatchPage from "./pages/PublicMatchPage";
import LiveMatchesPage from "./pages/LiveMatchesPage";
import FixturesPage from "./pages/FixturesPage";
import ResultsPage from "./pages/ResultsPage";
import LeaguesPublicPage from "./pages/LeaguesPublicPage";
import LeagueDetailPage from "./pages/LeagueDetailPage";
import AuthModal from "./components/AuthModal";
import App from "./App";

function InviteLanding({ token }: { token: string }) {
  const [showAuth, setShowAuth] = useState(true);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold mb-2">You're Invited to WatchWicket</h1>
        <p className="text-slate-400 mb-6">
          Complete your signup using the invitation code we've pre-filled for you.
        </p>
        <button
          onClick={() => setShowAuth(true)}
          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
        >
          Create Account
        </button>
      </div>
      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          initialMode="signup"
          initialInvitationToken={token}
        />
      )}
    </div>
  );
}

export default function AppRouter() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [oauthError, setOauthError] = useState<string | null>(null);

  useEffect(() => {
    const handle = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener("popstate", handle);
    return () => window.removeEventListener("popstate", handle);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const errorParam = searchParams.get("error");
    if (errorParam && (searchParams.get("error_code") || searchParams.get("error_description"))) {
      const description = searchParams.get("error_description") || errorParam;
      setOauthError(description);
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [currentPath]);

  if (oauthError && currentPath === "/") {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-6 text-6xl">⚠️</div>
          <h1 className="text-2xl font-bold mb-2 text-red-400">Sign-in Failed</h1>
          <p className="text-slate-300 mb-4">{oauthError}</p>
          <p className="text-slate-400 text-sm">Please try signing in again.</p>
        </div>
      </div>
    );
  }

  if (currentPath === "/auth-debug") return <AuthDebug />;
  if (currentPath === "/auth/callback") return <AuthCallback />;
  if (currentPath === "/admin/invites") return <AdminInvites />;

  const inviteMatch = currentPath.match(/^\/invite\/(.+)$/);
  if (inviteMatch) return <InviteLanding token={decodeURIComponent(inviteMatch[1])} />;

  const matchIdMatch = currentPath.match(/^\/match\/(.+)$/);
  if (matchIdMatch) return <PublicMatchPage matchId={matchIdMatch[1]} />;

  const leagueIdMatch = currentPath.match(/^\/leagues\/(.+)$/);
  if (leagueIdMatch) return <LeagueDetailPage leagueId={leagueIdMatch[1]} />;

  if (currentPath === "/live") return <LiveMatchesPage />;
  if (currentPath === "/fixtures") return <FixturesPage />;
  if (currentPath === "/results") return <ResultsPage />;
  if (currentPath === "/leagues") return <LeaguesPublicPage />;

  return <App />;
}
